import * as CL from '@design-system/component-library';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import {
  deviceEnrollmentProgramAdeIdInfoMsg,
  deviceEnrollmentProgramAliasInfoMsg,
  enrollmentAliasPlaceHolderMsg,
  t,
} from '../../../../common/i18n/index.js';
import { TextInput } from '../../../../common/react-hook-form/fields/TextInput.js';
import { Dropdown } from '../../../Dropdown/Dropdown.js';
import { useCheckoutContext } from '../../context/CheckoutContextCoCo.js';
import {
  EnrollmentProgramValue,
  NO_DEVICE_ENROLLMENT_PROGRAM_VALUE,
  getEnrollmentProgramDisplayLabel,
  getMatchingEnrollmentProgramsForCart,
} from '../../utils/deviceEnrollmentProgramUtilsCoCo.js';
import type { ProductOfferingCartItems } from '../../../../common/types/shoppingBasketCoCo.js';
import type { PlaceOrderFormDataCoCo, PlaceOrderItemUnitCoCo } from '../../placeOrderTypesCoCo.js';
import type { ReactNode } from 'react';

import './DeviceEnrollmentProgramSectionCoCo.scss';

const silentUpdate = { shouldDirty: false, shouldTouch: false };
const userUpdate = { shouldDirty: true, shouldTouch: true };

export interface DeviceEnrollmentProgramSectionProps {
  orderItem: PlaceOrderItemUnitCoCo;
  basketItem: ProductOfferingCartItems;
}

interface EnrollmentProgramDetailsProps {
  label: string;
  tooltipText: string;
  children: ReactNode;
}

const EnrollmentProgramDetails = ({ label, tooltipText, children }: EnrollmentProgramDetailsProps) => (
  <div className={dsClass.MARGIN_TOP_2}>
    <div className={`${dsClass.INPUT_LABELAREA_LABEL} ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}>
      <span>{label}</span>
      <span className={`${dsClass.DISPLAY_INLINE_FLEX} ${dsClass.MARGIN_LEFT_1} of-device-enrollment-program__tooltip`}>
        <CL.Tooltip
          triggerElement={<CL.Icon icon="information" size="s" />}
          placement="top"
          i18n_tooltip_contentText={tooltipText}
          className={dsClass.MARGIN_LEFT_3}
        />
      </span>
    </div>

    {children}
  </div>
);

export const DeviceEnrollmentProgramSectionCoCo = ({ orderItem, basketItem }: DeviceEnrollmentProgramSectionProps) => {
  const { customerInfo } = useCheckoutContext();
  const { getValues, setValue, watch } = useFormContext<PlaceOrderFormDataCoCo>();
  const matchingEnrollmentPrograms = getMatchingEnrollmentProgramsForCart([basketItem], customerInfo.characteristic);
  const orderItemUnits = watch('products.placeOrderItemUnits') ?? [];

  const orderItemIndex = orderItemUnits.findIndex(
    unit => unit.basketItemId === orderItem.basketItemId && unit.basketItemIndex === orderItem.basketItemIndex
  );
  const currentUnit = orderItemUnits[orderItemIndex];

  const hasEnrollmentSelection =
    currentUnit?.enrollmentProgram !== undefined || currentUnit?.deviceEnrollment !== undefined;
  const hasOptedOut = currentUnit?.deviceEnrollment === 'false';
  // A saved program that is no longer available falls back to the first matching one.
  const selectedProgram = hasOptedOut
    ? undefined
    : (matchingEnrollmentPrograms.find(({ program }) => program === currentUnit?.enrollmentProgram) ??
      matchingEnrollmentPrograms[0]);
  const selectedValue = selectedProgram?.program ?? NO_DEVICE_ENROLLMENT_PROGRAM_VALUE;

  const isApple = selectedProgram?.program === EnrollmentProgramValue.APPLE;
  const isSamsung = selectedProgram?.program === EnrollmentProgramValue.SAMSUNG;
  const enrollmentCustomerId = isApple
    ? (selectedProgram.customerIds.find(id => id === currentUnit?.enrollmentCustomerId) ??
      selectedProgram.customerIds[0])
    : undefined;
  const enrollmentAlias = isSamsung ? currentUnit?.enrollmentAlias : undefined;

  useEffect(() => {
    if (orderItemIndex < 0 || (matchingEnrollmentPrograms.length === 0 && !hasEnrollmentSelection)) {
      return;
    }

    const unit = getValues('products.placeOrderItemUnits')[orderItemIndex];
    const nextProgram = selectedValue === NO_DEVICE_ENROLLMENT_PROGRAM_VALUE ? '' : selectedValue;
    const nextDeviceEnrollment = selectedValue === NO_DEVICE_ENROLLMENT_PROGRAM_VALUE ? 'false' : 'true';

    if (unit.enrollmentProgram !== nextProgram) {
      setValue(`products.placeOrderItemUnits.${orderItemIndex}.enrollmentProgram`, nextProgram, silentUpdate);
    }
    if (unit.deviceEnrollment !== nextDeviceEnrollment) {
      setValue(`products.placeOrderItemUnits.${orderItemIndex}.deviceEnrollment`, nextDeviceEnrollment, silentUpdate);
    }
    if (unit.enrollmentCustomerId !== enrollmentCustomerId) {
      setValue(
        `products.placeOrderItemUnits.${orderItemIndex}.enrollmentCustomerId`,
        enrollmentCustomerId,
        silentUpdate
      );
    }
    if (unit.enrollmentAlias !== enrollmentAlias) {
      setValue(`products.placeOrderItemUnits.${orderItemIndex}.enrollmentAlias`, enrollmentAlias, silentUpdate);
    }
  }, [
    selectedValue,
    enrollmentCustomerId,
    enrollmentAlias,
    hasEnrollmentSelection,
    orderItemIndex,
    matchingEnrollmentPrograms.length,
    getValues,
    setValue,
  ]);

  if (orderItemIndex < 0 || matchingEnrollmentPrograms.length === 0) {
    return null;
  }

  const handleChange = (value: string) => {
    if (value === selectedValue) {
      return;
    }
    const nextProgram = matchingEnrollmentPrograms.find(({ program }) => program === value);
    const nextCustomerId =
      nextProgram?.program === EnrollmentProgramValue.APPLE ? nextProgram.customerIds[0] : undefined;
    setValue(
      `products.placeOrderItemUnits.${orderItemIndex}.enrollmentProgram`,
      value === NO_DEVICE_ENROLLMENT_PROGRAM_VALUE ? '' : value,
      userUpdate
    );
    setValue(
      `products.placeOrderItemUnits.${orderItemIndex}.deviceEnrollment`,
      nextProgram ? 'true' : 'false',
      userUpdate
    );
    setValue(`products.placeOrderItemUnits.${orderItemIndex}.enrollmentCustomerId`, nextCustomerId, userUpdate);
    setValue(`products.placeOrderItemUnits.${orderItemIndex}.enrollmentAlias`, undefined, userUpdate);
  };

  return (
    <div className="of-order-item-unit-coco__config-box of-device-enrollment-program">
      <CL.RadioGroup
        label={t.Q03S('Device enrollment program')}
        value={selectedValue}
        onChange={value => handleChange(value ?? NO_DEVICE_ENROLLMENT_PROGRAM_VALUE)}
        i18n_radiogroup_infoText={t.Z20L(
          'Device enrollment programs ensure managed provisioning for your company devices.'
        )}
      >
        {matchingEnrollmentPrograms.map(({ program, customerIds }) => (
          <CL.RadioV2 key={program} value={program} className="of-device-enrollment-program__radio">
            <div className="of-device-enrollment-program__program">
              <div className={dsClass.DISPLAY_BLOCK}>{getEnrollmentProgramDisplayLabel(program)}</div>
              {selectedValue === program && program === EnrollmentProgramValue.SAMSUNG && (
                <TextInput
                  name={`products.placeOrderItemUnits.${orderItemIndex}.enrollmentAlias`}
                  type="text"
                  label={t.IBVP(enrollmentAliasPlaceHolderMsg)}
                  placeholder=" "
                  tooltip={t.BJRZ(deviceEnrollmentProgramAliasInfoMsg)}
                  required={false}
                  maxLength={64}
                  wrapperClassName={`${dsClass.PADDING_TOP_2} of-device-enrollment-program__input`}
                />
              )}
              {selectedValue === program && program === EnrollmentProgramValue.APPLE && (
                <EnrollmentProgramDetails
                  label={getEnrollmentProgramDisplayLabel(program)}
                  tooltipText={t.BJRZ(deviceEnrollmentProgramAdeIdInfoMsg)}
                >
                  {customerIds.length > 1 ? (
                    <Dropdown
                      className={`${dsClass.MARGIN_TOP_2} of-device-enrollment-program__dropdown`}
                      i18n_dropdown_ariaLabel={getEnrollmentProgramDisplayLabel(program)}
                      items={customerIds.map(customerId => ({ label: customerId, value: customerId }))}
                      selectedValue={enrollmentCustomerId}
                      onValueChange={element => {
                        const { value } = element.dataset;
                        if (value) {
                          setValue(
                            `products.placeOrderItemUnits.${orderItemIndex}.enrollmentCustomerId`,
                            value,
                            userUpdate
                          );
                        }
                      }}
                    />
                  ) : (
                    <div className={dsClass.MARGIN_TOP_2}>{enrollmentCustomerId}</div>
                  )}
                </EnrollmentProgramDetails>
              )}
            </div>
          </CL.RadioV2>
        ))}
        <CL.RadioV2
          key={NO_DEVICE_ENROLLMENT_PROGRAM_VALUE}
          value={NO_DEVICE_ENROLLMENT_PROGRAM_VALUE}
          label={t.FBMB('No device enrolment program')}
        />
      </CL.RadioGroup>
    </div>
  );
};
